import '~src/assets/fonts/fonts.css';

import { createGlobalStyle } from 'styled-components';
import { normalize } from 'styled-normalize';

import { customScrollbar } from './mixin';
import { maxWidth, vars } from './theme';

export const GlobalStyle = createGlobalStyle`
  :root {
    ${vars}
  }

  ${normalize}

  /* Fix antialiasing and Disable user select on everything but texts */
  *, *::before, *::after {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    box-sizing: border-box;
    user-select: none;
  }

  p, h1, h2, h3, h4, h5, h6, blockquote, pre, ul, ol, li, table, tr, th, td, input, textarea {
    user-select: text;
  }

  [hidden] {
    display: none !important;
    appearance: initial;
    background-color: initial;
    cursor: default;
    padding: initial;
    border: initial;
  }

  /*
    The following CSS rules leverage classes that are dynamically applied to elements by
    focus-visible-polyfill. The purpose of the polyfill is to hide or show focus rings for
    users based on  how they navigate the site. Generally, users who navigate with their
    keyboard will still see focus rings while users who navigate with mouse/touch will not.
    For more details: https://github.com/WICG/focus-visible
  */ 

  /*
    This will hide the focus indicator if the element receives focus via the mouse,
    but it will still show up on keyboard focus.
  */
  .js-focus-visible :focus:not(.focus-visible) {
    outline: none;
  }


  button.focus-visible,
  input.focus-visible + .checkable {
    outline: none;
    box-shadow: 0 0 0 2px var(--color--primary-main);
  }

  html {
    box-sizing: border-box;
    font-size: 16px;
  }

  * {
    ${customScrollbar}
  }

  body {
    text-shadow: rgba(0, 0, 0, .01) 0 0 1px;
    color: var(--color--text-primary);
    font-family: var(--font--body);
    font-weight: normal;
    font-size: 1em;
    line-height: 1.625;
  }

  h1, h2, h3, h4, h5, h6  {
    font-family: var(--font--heading);
    line-height: 1;
    margin: 0;
    font-weight: lighter;
    a {
      color: inherit !important;
      text-decoration: none !important;
      font-weight: inherit !important
    }
  }
  
  h1 {
    font-size: 3.75em;
    line-height: 1;
    color: var(--color--secondary-main);
    ${maxWidth.tablet`
      font-size: 3em;
    `}
  }

  h2 {
    font-size: 2.2em;
    line-height: 1.8;
    text-transform: uppercase;
    margin-bottom: 1em;
    color: var(--color--secondary-main);
    ${maxWidth.tablet`
      font-size: 2.1em;
    `}
    ${maxWidth.tablet`
      font-size: 1.8em;
    `}
  }

  h3 {
    font-size: 1.5em;
    line-height: 1.3em;
    color: var(--color--text-primary);
    margin-bottom: 0.4em;
    ${maxWidth.tablet`
      font-size: 1.5em;
    `}
  }

  h4 {
    font-size: 1.3em;
    line-height: 1.3em;
    color: var(--color--text-primary);
    margin-bottom: 0.4em;
    ${maxWidth.tablet`
      font-size: 1.15em;
    `}
  }

  h5 {
    font-size: 1.2em;
    line-height: 1.3em;
    color: var(--color--text-primary);
    margin-bottom: 0.4em;
  }

  h6 {
    font-size: 1.1em;
    line-height: 1.3em;
    color: var(--color--text-primary);
    margin-bottom: 0.4em;
  }

  p {
    display: block;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0;
    margin-inline-end: 0;
  }

  strong {
    font-weight: bold;
  }

  ul,
  ol {
    list-style-type: none;
    margin: 0;
    padding: 0
  }

  *, ::after, ::before {
    box-sizing: inherit;
  }

  ::selection,
  ::-moz-selection  {
    color: var(--color--text-secondary);
    opacity: 1;
    background: var(--color--primary-main);
  }

  img {
    display: block;
  	width: 100%;
  	height: auto;
  }

  a {
    color: var(--color--secondary-main);
    text-decoration: none;
    transition: all 300ms ease-in-out;
    &:active,
    &:hover,
    &:focus {
      /* color: var(--color--primary-main); */
      outline:0;
    }
  }

  button,
  input,
  textarea,
  select {
    color: var(--color--secondary-main);
    font-family: inherit;
    font-size: inherit;
    background: none;
    border: none;
    appearance: none;
    border-radius: 0;
    resize: none;
    &:invalid {
      box-shadow: none;
    }
    &:focus {
      outline: 5px auto #5E9ED6;
      outline: 5px auto -webkit-focus-ring-color;
    }
  }

  a:focus {
    outline: 5px auto #5E9ED6;
    outline: 5px auto -webkit-focus-ring-color;
  }

  button {
    padding: 0;
  }

  body:not(.user-is-tabbing) button:focus,
  body:not(.user-is-tabbing) input:focus,
  body:not(.user-is-tabbing) select:focus,
  body:not(.user-is-tabbing) textarea:focus,
  body:not(.user-is-tabbing) a:focus  {
    outline: none;
  }

  ::-webkit-input-placeholder {
    color: gray;
  }

  ::-moz-placeholder {
    color: gray; opacity: 1;
  }

  :-ms-input-placeholder {
    color: gray;
  }

  :-moz-placeholder {
    color: gray; opacity: 1;
  }

  select:invalid, select option[value=''] {
    color: gray !important;
  }

  /* Hide content until mount */

  .initial {
    opacity: 0;
  }

  .loaded {
    opacity: 1;
  }

  /* Animations */
  .fadeInUp {
    animation-name: fadeInUp;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translate3d(0, 100%, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  /* Scrolling related clases */

  .contain {
    overflow: hidden;
  }

  /* Nprogress */

  .nprogress-busy {
    cursor: wait;
  }

  #nprogress .peg {
    display: none !important;
  }

  #nprogress .bar {
    z-index: 1000;
    height: 4px !important;
  }


`;
